import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useAppDispatch } from "../../store/hooks";
import { UIActions } from "../../store/ui-slice";
import modalClasses from "../UI/ConfirmModal.module.css";
import classes from "./DiscardChangesConfirm.module.css";
import { t } from "../../i18n";

// Asked when leaving the form with unsaved changes. It used to be "Discard
// changes?" with a "Discard" button ("Отменить изменения?" / "Отменить"),
// which reads as "cancel" - the very thing a tester expected the other
// button to do. Every answer now names its outcome, and the hierarchy says
// which is which: Save is the filled primary, Don't save is red, and staying
// in the form is a quiet link (as are a click outside and Escape).
const DiscardChangesConfirm = () => {
  const dispatch = useAppDispatch();
  const { pendingFormAction, saveBeforeLeave, canSaveForm } = useSelector(
    (state: RootState) => state.ui
  );
  const isOpen = pendingFormAction !== null && !saveBeforeLeave;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dispatch(UIActions.cancelLeaveForm());
      }
    };
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, [isOpen, dispatch]);

  if (!isOpen) {
    return null;
  }

  const keepEditingHandler = () => dispatch(UIActions.cancelLeaveForm());

  const discardHandler = () => {
    dispatch(UIActions.cancelLeaveForm());
    dispatch(UIActions.setFormDirty(false));
    dispatch(pendingFormAction);
  };

  // EventForm picks this up, saves, and only then carries on where the user
  // was going - or stays put if the save fails.
  const saveHandler = () => dispatch(UIActions.saveBeforeLeave());

  return (
    <div className={modalClasses.overlay} onClick={keepEditingHandler}>
      <div
        className={modalClasses.card}
        role="alertdialog"
        aria-labelledby="unsaved-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div id="unsaved-title" className={modalClasses.title}>
          {canSaveForm ? t.unsavedChanges.title : t.unsavedChanges.noTitleTitle}
        </div>
        <div className={modalClasses.message}>
          {canSaveForm
            ? t.unsavedChanges.message
            : t.unsavedChanges.noTitleMessage}
        </div>
        <div className={modalClasses.actions}>
          <button
            type="button"
            className={classes.discardButton}
            onClick={discardHandler}
          >
            {t.unsavedChanges.discard}
          </button>
          {canSaveForm && (
            <button
              type="button"
              className={modalClasses.confirmButton}
              onClick={saveHandler}
              autoFocus
            >
              {t.unsavedChanges.save}
            </button>
          )}
        </div>
        <button
          type="button"
          className={classes.keepEditing}
          onClick={keepEditingHandler}
          autoFocus={!canSaveForm}
        >
          {t.unsavedChanges.keepEditing}
        </button>
      </div>
    </div>
  );
};

export default DiscardChangesConfirm;
